// src/pages/OnboardingBusinessDashboard.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { authenticatedFetch } from '../utils/api';

// ✅ FIXED: No trailing spaces in fallback URL
const API_BASE = (process.env.REACT_APP_API_URL || 'https://fslapi.devformula.com/api').trim();

function OnboardingBusinessDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('business');
  const [businessData, setBusinessData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ✅ NEW: State for KYC Explanation Modal
  const [showKycModal, setShowKycModal] = useState(true);

  const getSessionId = () => {
    return location.state?.sessionId 
      || JSON.parse(localStorage.getItem('userSession') || '{}')?.sessionId 
      || null;
  };

  // ✅ Check onboarding status from localStorage BEFORE API call (for reload protection)
  useEffect(() => {
    const checkOnboardingStatus = () => {
      const session = JSON.parse(localStorage.getItem('userSession') || '{}');
      
      if (session.isOnboarded === true || session.isOnboarded === 1) {
        console.log('🔄 User already onboarded - redirecting to /dashboard');
        navigate('/dashboard', { replace: true });
        return true;
      }
      return false;
    };

    if (!checkOnboardingStatus()) {
      AOS.init({ duration: 1000, once: true });
      fetchCompanyBySession();
    }
  }, [navigate]);

  const fetchCompanyBySession = async () => {
    const sessionId = getSessionId();
    
    if (!sessionId) {
      setError('No active session. Please log in again.');
      setLoading(false);
      toast.warning('Session not found');
      setTimeout(() => navigate('/login', { replace: true }), 2000);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const apiUrl = `${API_BASE}/HMO/get-company-by-session`;
      const payload = { sessionId: sessionId.toString() };
      
      console.log('🔍 API Call:', { url: apiUrl, payload });

      const result = await authenticatedFetch(apiUrl, 'POST', payload);

      if (result?.status === 'fail' || result?.status === 'error') {
        if (result?.message?.toLowerCase().includes('expired') || 
            result?.message?.toLowerCase().includes('not found')) {
          handleSessionInvalid(result.message);
          return;
        }
        throw new Error(result.message || 'Failed to fetch company data');
      }

      setBusinessData(result.data);
      
      const existing = JSON.parse(localStorage.getItem('userSession') || '{}');
      localStorage.setItem('userSession', JSON.stringify({
        ...existing,
        ...result.data,
        lastFetched: new Date().toISOString()
      }));

      const isOnboarded = result.data?.isOnboarded 
        ?? result.data?.IsOnboarded 
        ?? existing.isOnboarded 
        ?? false;
      
      if (isOnboarded === true || isOnboarded === 1) {
        console.log('✅ User onboarded - redirecting to /dashboard');
        toast.success('Onboarding complete! Redirecting to dashboard...');
        setTimeout(() => {
          navigate('/dashboard', { replace: true });
        }, 1500);
        return; 
      }

    } catch (err) {
      console.error('💥 Fetch Error:', err);
      
      if (err.message?.includes('404') || err.message?.includes('Not Found')) {
        setError('Service temporarily unavailable. Please try again later.');
        toast.error('API endpoint not available');
        return;
      }
      
      if (err.message?.includes('Failed to fetch') || err.name === 'TypeError') {
        setError('Cannot connect to server. Check your internet.');
        toast.error('Connection error');
        return;
      }
      
      if (err.message?.includes('expired') || err.message?.includes('401')) {
        handleSessionInvalid(err.message);
        return;
      }
      
      setError(err.message || 'Failed to load business information');
      toast.error(err.message || 'Could not load data');
      
    } finally {
      setLoading(false);
    }
  };

  const handleSessionInvalid = (message) => {
    setError(message || 'Session expired');
    toast.warning('Please log in again');
    localStorage.removeItem('userSession');
    setTimeout(() => {
      navigate('/login', { replace: true, state: { from: location.pathname } });
    }, 2000);
  };

  const handleLogout = () => {
    localStorage.removeItem('userSession');
    toast.success('Logged out successfully');
    navigate('/login', { replace: true });
  };

  const getProductsArray = (productsString) => {
    if (!productsString) return [];
    
    if (Array.isArray(productsString)) {
      return productsString.map(p => {
        if (p === null || p === undefined) return '';
        if (typeof p === 'object' && p !== null) {
          const extracted = p.name || p.value || p.label || p.title || p.toString?.();
          if (extracted && extracted !== '[object Object]') return String(extracted);
          const jsonStr = JSON.stringify(p);
          return jsonStr !== '{}' && jsonStr !== '[object Object]' ? jsonStr : '';
        }
        const str = String(p);
        return str !== '[object Object]' ? str : '';
      }).filter(p => p && p.trim());
    }
    
    try {
      const parsed = JSON.parse(productsString);
      if (Array.isArray(parsed)) {
        return parsed.map(p => {
          if (p === null || p === undefined) return '';
          if (typeof p === 'object' && p !== null) {
            const extracted = p.name || p.value || p.label || p.title || p.toString?.();
            if (extracted && extracted !== '[object Object]') return String(extracted);
            const jsonStr = JSON.stringify(p);
            return jsonStr !== '{}' && jsonStr !== '[object Object]' ? jsonStr : '';
          }
          const str = String(p);
          return str !== '[object Object]' ? str : '';
        }).filter(p => p && p.trim());
      }
    } catch {}
    
    return String(productsString)
      .split(',')
      .map(p => {
        const str = String(p).trim();
        return str !== '[object Object]' ? str : '';
      })
      .filter(p => p);
  };

  if (loading) {
    return (
      <div className="business-dashboard-page d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <div className="text-center">
          <div className="spinner-border text-orange mb-3" role="status" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="text-muted">Loading your business information...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="business-dashboard-page d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <div className="card shadow-sm border-0" style={{ maxWidth: '500px', width: '100%' }}>
          <div className="card-body p-4 text-center">
            <i className="fas fa-exclamation-triangle text-warning mb-3" style={{ fontSize: '48px' }}></i>
            <h5 className="mb-2">Unable to Load Data</h5>
            <p className="text-muted mb-4">{error}</p>
            <div className="d-flex gap-2 justify-content-center">
              <button className="theme-btn style-one" onClick={() => navigate('/login')}>Go to Login</button>
              <button className="btn btn-outline-secondary" onClick={fetchCompanyBySession}>
                <i className="fas fa-redo me-1"></i> Retry
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const displayData = {
    name: (() => {
      const val = businessData?.businessName || businessData?.BusinessName || 
        `${businessData?.firstName || businessData?.FirstName || ''} ${businessData?.lastName || businessData?.LastName || ''}`.trim();
      const str = String(val || 'N/A');
      return str !== '[object Object]' ? str : 'N/A';
    })(),
    email: (() => {
      const val = businessData?.email || businessData?.Email || 'N/A';
      const str = String(val);
      return str !== '[object Object]' ? str : 'N/A';
    })(),
    phone: (() => {
      const val = businessData?.businessPhone || businessData?.BusinessPhone || 
        businessData?.phoneNumber || businessData?.PhoneNumber || 'N/A';
      const str = String(val);
      return str !== '[object Object]' ? str : 'N/A';
    })(),
    state: (() => {
      const val = businessData?.stateName || businessData?.StateName || 
                  businessData?.stateOfOperation || businessData?.StateOfOperation || 'N/A';
      const str = String(val);
      return str !== '[object Object]' ? str : 'N/A';
    })(),
    category: (() => {
      const val = businessData?.businessCategory || businessData?.BusinessCategory || 'N/A';
      const str = String(val);
      return str !== '[object Object]' ? str : 'N/A';
    })(),
    sector: (() => {
      const val = businessData?.businessSector || businessData?.BusinessSector || 'N/A';
      const str = String(val);
      return str !== '[object Object]' ? str : 'N/A';
    })(),
    products: getProductsArray(businessData?.productsOfInterest || businessData?.ProductsOfInterest),
    onboarded: businessData?.isOnboarded ?? businessData?.IsOnboarded ?? false,
    title: (() => {
      const val = businessData?.title || businessData?.Title || 'N/A';
      const str = String(val);
      return str !== '[object Object]' ? str : 'N/A';
    })(),
    hearAbout: (() => {
      const val = businessData?.hearAbout || businessData?.HearAbout || 'N/A';
      const str = String(val);
      return str !== '[object Object]' ? str : 'N/A';
    })(),
    createdAt: (() => {
      const dateVal = businessData?.createdDate || businessData?.CreatedDate;
      if (dateVal) {
        try {
          return new Date(dateVal).toLocaleDateString('en-NG', { year: 'numeric', month: 'long', day: 'numeric' });
        } catch { return 'N/A'; }
      }
      return 'N/A';
    })()
  };

  const timelineSteps = [
    { id: 1, label: 'Account Created', date: String(displayData.createdAt || 'N/A'), completed: true },
    { 
      id: 2, label: 'Email Verified', 
      date: (businessData?.isEmailVerified ?? businessData?.IsEmailVerified) ? 'Verified' : 'Pending', 
      completed: businessData?.isEmailVerified ?? businessData?.IsEmailVerified ?? false 
    },
    { 
      id: 3, label: 'Account Activation', 
      date: displayData.onboarded ? 'Activated' : 'Pending', 
      completed: displayData.onboarded 
    }
  ];

  const getUserInitials = () => {
    const f = (businessData?.firstName || businessData?.FirstName)?.[0] || 'U';
    const l = (businessData?.lastName || businessData?.LastName)?.[0] || 'S';
    return (f + l).toUpperCase();
  };

  const safeString = (value, fallback = 'N/A') => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'string') return value;
    if (typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (typeof value === 'object' && value !== null) {
      const extracted = value.name || value.value || value.label || value.title || value.toString?.();
      if (extracted && extracted !== '[object Object]') return String(extracted);
      try {
        const jsonStr = JSON.stringify(value);
        if (jsonStr && jsonStr !== '{}' && jsonStr !== '[object Object]') return jsonStr;
      } catch {}
      return fallback;
    }
    const str = String(value);
    return str !== '[object Object]' ? str : fallback;
  };

  return (
    <div className="business-dashboard-page">
      <ToastContainer position="top-right" autoClose={5000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light" />

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="sidebar-header">
          <div className="brand-logo">
            <img src="images/fsl_logo.png" alt="FSL Logo" className="fsl-logo" />
          </div>
        </div>
        <nav className="sidebar-nav">
          <div className="nav-section">
            <a href="/" className="nav-link active"><i className="fas fa-home"></i><span>Get Started</span></a>
          </div>
          <div className="nav-section">
            <div className="nav-section-title">DASHBOARD</div>
            <ul className="nav-list">
              <li><div className="menu-placeholder"></div></li>
              <li><div className="menu-placeholder"></div></li>
            </ul>
          </div>
          <div className="nav-section">
            <div className="nav-section-title">ADMIN</div>
            <ul className="nav-list">
              <li><div className="menu-placeholder"></div></li>
              <li><div className="menu-placeholder"></div></li>
            </ul>
          </div>
          <div className="nav-section">
            <div className="nav-section-title">PRODUCTS</div>
            <ul className="nav-list">
              <li><div className="menu-placeholder"></div></li>
              <li><div className="menu-placeholder"></div></li>
            </ul>
          </div>
          <div className="nav-section">
            <div className="nav-section-title">TRANSACTIONS</div>
            <ul className="nav-list">
              <li><div className="menu-placeholder"></div></li>
              <li><div className="menu-placeholder"></div></li>
            </ul>
          </div>
          <div className="nav-section">
            <div className="nav-section-title">ACCOUNT</div>
            <ul className="nav-list">
              <li>
                <button className="nav-link text-white border-0 bg-transparent w-100 text-start" onClick={() => navigate('/OnboardSettings')} style={{ cursor: 'pointer' }}>
                  <i className="fas fa-cog"></i><span>Settings</span>
                </button>
              </li>
              <li>
                <button className="nav-link text-white border-0 bg-transparent w-100 text-start" onClick={handleLogout} style={{ cursor: 'pointer' }}>
                  <i className="fas fa-sign-out-alt"></i><span>Logout</span>
                </button>
              </li>
            </ul>
          </div>
        </nav>
      </aside>

      {/* Main Content */}
      <div className="main-content">
        

        <header className="top-header">
          <div className="header-left">
            <button className="menu-toggle d-lg-none" onClick={() => setSidebarOpen(!sidebarOpen)} aria-label="Toggle sidebar"><i className="fas fa-bars"></i></button>
            <h1 className="page-title"><i className="fas fa-th-list me-2"></i>Business Information</h1>
          </div>
          <div className="header-right">
            <button className="header-icon" onClick={() => navigate('/OnboardSettings')} title="Go to Settings" style={{ cursor: 'pointer' }}>
              <i className="fas fa-cog"></i>
            </button>
            <div className="user-avatar" title={`${businessData?.firstName || businessData?.FirstName || 'User'} ${businessData?.lastName || businessData?.LastName || ''}`.trim()}>{getUserInitials()}</div>
          </div>
        </header>

        <div className="content-wrapper">
          <div className="row">
            <div className="col-lg-8">
              <div className="tabs-container mb-4">
                <div className="nav nav-tabs" role="tablist">
                  <button className={`nav-link ${activeTab === 'business' ? 'active' : ''}`} onClick={() => setActiveTab('business')} role="tab">Business Information</button>
                  <button className={`nav-link ${activeTab === 'contact' ? 'active' : ''}`} onClick={() => setActiveTab('contact')} role="tab">Contact Person Information</button>
                </div>
              </div>

              <div className="card mb-4" data-aos="fade-up">
                <div className="card-body p-4">
                  <h5 className="text-orange mb-4">Business Information</h5>
                  <div className="row">
                    <div className="col-md-12 mb-4">
                      <label className="text-muted small mb-1">Business Name</label>
                      <p className="fw-semibold mb-0">{displayData.name}</p>
                    </div>
                    <div className="col-md-6 mb-4">
                      <label className="text-muted small mb-1">Email</label>
                      <p className="fw-semibold mb-0 text-truncate" title={displayData.email} style={{ 
                        maxWidth: '100%', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        wordBreak: 'break-all'
                      }}>
                        <a href={`mailto:${displayData.email}`} className="text-decoration-none text-dark">{displayData.email}</a>
                      </p>
                    </div>
                    <div className="col-md-6 mb-4">
                      <label className="text-muted small mb-1">Phone Number</label>
                      <p className="fw-semibold mb-0">
                        <a href={`tel:${displayData.phone}`} className="text-decoration-none text-dark">{displayData.phone}</a>
                      </p>
                    </div>
                    <div className="col-md-4 mb-4">
                      <label className="text-muted small mb-1">State of Operation</label>
                      <p className="fw-semibold mb-0">{displayData.state}</p>
                    </div>
                    <div className="col-md-4 mb-4">
                      <label className="text-muted small mb-1">Business Category</label>
                      <p className="fw-semibold mb-0">{displayData.category}</p>
                    </div>
                    <div className="col-md-4 mb-4">
                      <label className="text-muted small mb-1">Business Sector</label>
                      <p className="fw-semibold mb-0">{displayData.sector}</p>
                    </div>
                    
                    <div className="col-12 mb-4">
                      <label className="text-muted small mb-1">Products of Interest</label>
                      {displayData.products && displayData.products.length > 0 ? (
                        <div className="product-tags">
                          {displayData.products.map((product, index) => {
                            const productStr = safeString(product, 'Unknown');
                            return <span key={index} className="product-tag">{productStr}</span>;
                          })}
                        </div>
                      ) : (
                        <p className="text-muted mb-0">No products selected</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {activeTab === 'contact' && (
                <div className="card mb-4" data-aos="fade-up" data-aos-delay="100">
                  <div className="card-body p-4">
                    <h5 className="text-orange mb-4">Contact Person Information</h5>
                    <div className="row">
                      <div className="col-md-6 mb-4">
                        <label className="text-muted small mb-1">Full Name</label>
                        <p className="fw-semibold mb-0">{safeString(businessData?.firstName)} {safeString(businessData?.lastName)}</p>
                      </div>
                      <div className="col-md-6 mb-4">
                        <label className="text-muted small mb-1">Title/Position</label>
                        <p className="fw-semibold mb-0">{displayData.title}</p>
                      </div>
                      <div className="col-md-6 mb-4">
                        <label className="text-muted small mb-1">Email</label>
                        <p className="fw-semibold mb-0 text-truncate" title={safeString(businessData?.email, '')} style={{ 
                          maxWidth: '100%', 
                          overflow: 'hidden', 
                          textOverflow: 'ellipsis',
                          wordBreak: 'break-all'
                        }}>
                          <a href={`mailto:${safeString(businessData?.email, '')}`} className="text-decoration-none text-dark">{safeString(businessData?.email)}</a>
                        </p>
                      </div>
                      <div className="col-md-6 mb-4">
                        <label className="text-muted small mb-1">Phone Number</label>
                        <p className="fw-semibold mb-0">
                          <a href={`tel:${safeString(businessData?.phoneNumber, '')}`} className="text-decoration-none text-dark">{safeString(businessData?.phoneNumber)}</a>
                        </p>
                      </div>
                      <div className="col-md-6 mb-4">
                        <label className="text-muted small mb-1">How did you hear about us?</label>
                        <p className="fw-semibold mb-0">{displayData.hearAbout}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="card mb-4" data-aos="fade-up" data-aos-delay="100">
                <div className="card-body p-4">
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center">
                      <div className="support-icon me-3"><i className="fas fa-headset"></i></div>
                      <div><p className="mb-0 fw-semibold">Expert support available to you <strong>24/7</strong></p></div>
                    </div>
                    <a href="/support" className="btn btn-link text-orange text-decoration-none">Contact Support <i className="fas fa-external-link-alt ms-1"></i></a>
                  </div>
                </div>
              </div>

              <div className="alert-congratulations" data-aos="fade-up" data-aos-delay="200">
                <h5 className="text-orange mb-2">Congratulations</h5>
                <p className="mb-0 text-muted">You've successfully scheduled an onboarding call with one of our team members. Soon, you'll engage in a discussion about your business and how we can enhance your experience.</p>
              </div>
            </div>

            <div className="col-lg-4">
              <div className="card" data-aos="fade-left">
                <div className="card-body p-4">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <div><h5 className="mb-1">Account Status</h5><small className="text-muted">Track your account status</small></div>
                    <span className={`badge ${displayData.onboarded ? 'bg-success' : 'bg-warning text-dark'}`}>
                      {displayData.onboarded ? 'Active' : 'Onboarding'}
                    </span>
                  </div>
                  <div className="timeline">
                    {timelineSteps.map((step, index) => (
                      <div key={step.id} className="timeline-item">
                        <div className={`timeline-marker ${step.completed ? 'completed' : 'pending'}`}>
                          {step.completed ? <i className="fas fa-check"></i> : <div className="pending-dot"></div>}
                        </div>
                        {index < timelineSteps.length - 1 && <div className={`timeline-line ${step.completed ? 'completed' : ''}`}></div>}
                        <div className="timeline-content">
                          <h6 className={`mb-1 ${step.completed ? 'text-orange' : 'text-muted'}`}>{step.label}</h6>
                          <small className={step.completed ? 'text-muted' : 'text-warning'}>{safeString(step.date)}</small>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ✅ NEW: KYC EXPLANATION MODAL */}
      {showKycModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.6)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content custom-modal-content">
              <div className="modal-header border-0 pb-0">
                <h5 className="modal-title fw-bold d-flex align-items-center gap-2" style={{ color: 'var(--primary-orange)' }}>
                  <i className="fas fa-shield-alt"></i> Complete Your KYC Verification
                </h5>
                <button type="button" className="btn-close" onClick={() => setShowKycModal(false)}></button>
              </div>
              <div className="modal-body pt-2">
                <p className="text-muted mb-3">
                  To fully activate your account and access all platform features, we need to verify your business identity through our Know Your Customer (KYC) process.
                </p>
                <div className="kyc-info-list mb-3">
                  <div className="d-flex align-items-start gap-2 mb-2">
                    <i className="fas fa-check-circle text-success mt-1"></i>
                    <span>Upload your <strong>National Identity Number (NIN)</strong> slip or card.</span>
                  </div>
                  <div className="d-flex align-items-start gap-2 mb-2">
                    <i className="fas fa-check-circle text-success mt-1"></i>
                    <span>Upload your <strong>CAC Registration Certificate</strong>.</span>
                  </div>
                  <div className="d-flex align-items-start gap-2 mb-2">
                    <i className="fas fa-check-circle text-success mt-1"></i>
                    <span>Your documents are <strong>encrypted and stored securely</strong>.</span>
                  </div>
                </div>
                <div className="alert alert-warning d-flex align-items-center gap-2 mb-0" style={{ backgroundColor: '#fffbeb', borderColor: '#fde68a', color: '#92400e', fontSize: '13px' }}>
                  <i className="fas fa-info-circle"></i>
                  <small>Completing KYC is mandatory to process transactions and avoid account restrictions.</small>
                </div>
              </div>
              <div className="modal-footer border-0 pt-0">
                <button type="button" className="btn btn-light px-4" onClick={() => setShowKycModal(false)}>Maybe Later</button>
                <button 
                  type="button" 
                  className="btn text-white px-4" 
                  style={{ backgroundColor: 'var(--primary-orange)' }} 
                  onClick={() => {
                    navigate('/OnboardSettings');
                    setShowKycModal(false);
                  }}
                >
                  <i className="fas fa-upload me-2"></i> Proceed to KYC Upload
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSS */}
      <style>{`
        :root { --primary-orange: #F89423; --dark-orange: #ea7c1f; --light-orange: #f2a128; --sidebar-bg: #f2a128; }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        ::-webkit-scrollbar { width: 8px; height: 8px; }
        ::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 10px; }
        ::-webkit-scrollbar-thumb { background: var(--primary-orange); border-radius: 10px; }
        ::-webkit-scrollbar-thumb:hover { background: var(--dark-orange); }
        .business-dashboard-page { display: flex; min-height: 100vh; background-color: #fafafa; font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; }
        .sidebar { width: 260px; background: var(--sidebar-bg); color: white; position: fixed; left: 0; top: 0; height: 100vh; overflow-y: auto; transition: transform 0.3s ease; z-index: 1000; }
        .sidebar-header { padding: 24px 20px; border-bottom: 1px solid rgba(255,255,255,0.1); background: white; }
        .brand-logo { display: flex; align-items: center; gap: 12px; color: #333; font-size: 20px; font-weight: 700; }
        .fsl-logo { height: 40px; width: auto; }
        .sidebar-nav { padding: 20px 0; }
        .nav-section { margin-bottom: 24px; }
        .nav-section-title { font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; color: rgba(255,255,255,0.7); padding: 0 20px; margin-bottom: 12px; }
        .nav-list { list-style: none; padding: 0; margin: 0; }
        .nav-link { display: flex; align-items: center; padding: 12px 20px; color: rgba(255,255,255,0.9); text-decoration: none; transition: all 0.3s ease; font-weight: 500; font-size: 14px; border-left: 3px solid transparent; }
        .nav-link i { width: 20px; margin-right: 12px; font-size: 16px; }
        .nav-link:hover { background-color: rgba(255,255,255,0.1); border-left-color: white; color: white; }
        .nav-link.active { background-color: rgba(255,255,255,0.15); border-left-color: white; color: white; font-weight: 600; }
        .menu-placeholder { height: 40px; background: rgba(255,255,255,0.15); border-radius: 6px; margin: 0 20px 8px 20px; }
        .main-content { flex: 1; margin-left: 260px; display: flex; flex-direction: column; background-color: #fafafa; }
        .test-mode-banner { background: #fff1f2; border-bottom: 1px solid #fecdd3; padding: 12px 30px; text-align: center; font-size: 14px; color: #be123c; }
        .test-mode-banner strong { color: #be123c; }
        .top-header { background: white; padding: 16px 30px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); display: flex; justify-content: space-between; align-items: center; position: sticky; top: 0; z-index: 100; }
        .header-left { display: flex; align-items: center; gap: 20px; }
        .menu-toggle { background: none; border: none; font-size: 20px; color: #374151; cursor: pointer; display: none; }
        .page-title { display: flex; align-items: center; color: #1f2937; font-size: 20px; font-weight: 600; margin: 0; }
        .header-right { display: flex; align-items: center; gap: 12px; }
        .country-selector { display: flex; align-items: center; padding: 8px 16px; background: #f3f4f6; border-radius: 8px; cursor: pointer; font-weight: 500; gap: 8px; border: none; font-size: 14px; }
        .header-icon { width: 40px; height: 40px; border-radius: 50%; border: none; background: #f3f4f6; color: #6b7280; cursor: pointer; position: relative; transition: all 0.3s ease; display: flex; align-items: center; justify-content: center; font-size: 16px; }
        .header-icon:hover { background: #e5e7eb; color: var(--primary-orange); }
        .user-avatar { width: 40px; height: 40px; border-radius: 50%; background: linear-gradient(135deg, var(--primary-orange) 0%, var(--dark-orange) 100%); color: white; display: flex; align-items: center; justify-content: center; font-weight: 600; cursor: pointer; font-size: 14px; }
        .content-wrapper { padding: 30px; flex: 1; }
        .tabs-container { background: white; border-radius: 8px; padding: 8px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); }
        .nav-tabs { border: none; gap: 4px; }
        .nav-tabs .nav-link { border: none; background: transparent; color: #6b7280; padding: 12px 24px; border-radius: 6px; font-weight: 500; cursor: pointer; transition: all 0.3s ease; }
        .nav-tabs .nav-link.active { background: var(--primary-orange); color: white; }
        .card { border-radius: 8px; box-shadow: 0 1px 3px rgba(0,0,0,0.05); background: white; border: none; }
        .text-orange { color: var(--primary-orange); }
        .product-tags { display: flex; flex-wrap: wrap; gap: 8px; }
        .product-tag { background: #1f2937; color: white; padding: 6px 12px; border-radius: 20px; font-size: 12px; font-weight: 500; }
        .support-icon { width: 48px; height: 48px; border-radius: 50%; background: var(--light-orange); color: var(--primary-orange); display: flex; align-items: center; justify-content: center; font-size: 20px; }
        .alert-congratulations { background: #f5f3ff; border: 1px solid #ddd6fe; border-radius: 8px; padding: 24px; }
        .timeline { position: relative; padding-left: 0; }
        .timeline-item { display: flex; align-items: flex-start; margin-bottom: 24px; position: relative; }
        .timeline-marker { width: 24px; height: 24px; border-radius: 50%; background: #f3f4f6; display: flex; align-items: center; justify-content: center; margin-right: 12px; flex-shrink: 0; z-index: 1; font-size: 12px; }
        .timeline-marker.completed { background: var(--primary-orange); color: white; }
        .timeline-marker.pending { background: white; border: 2px solid #fbbf24; }
        .timeline-marker .pending-dot { width: 8px; height: 8px; border-radius: 50%; background: #fbbf24; }
        .timeline-line { position: absolute; left: 11px; top: 24px; width: 2px; height: calc(100% - 24px); background: #e5e7eb; z-index: 0; }
        .timeline-line.completed { background: var(--primary-orange); }
        .timeline-content h6 { font-size: 14px; font-weight: 600; margin-bottom: 4px; }
        .timeline-content small { font-size: 12px; }
        
        /* ✅ NEW: Modal Custom Styles */
        .custom-modal-content { border-radius: 12px !important; border: none !important; box-shadow: 0 10px 25px rgba(0,0,0,0.2) !important; }
        .custom-modal-content .modal-header { border-bottom: 2px solid #f3f4f6 !important; }
        .custom-modal-content .modal-footer { border-top: 2px solid #f3f4f6 !important; }
        .custom-modal-content .form-control:focus, 
        .custom-modal-content .form-select:focus { 
          border-color: var(--primary-orange) !important; 
          box-shadow: 0 0 0 0.25rem rgba(248, 148, 35, 0.25) !important; 
        }
        .kyc-info-list { background: #f9fafb; padding: 16px; border-radius: 8px; border: 1px solid #e5e7eb; }

        @media (max-width: 992px) { .sidebar { transform: translateX(-100%); } .sidebar.sidebar-open { transform: translateX(0); } .main-content { margin-left: 0; } .menu-toggle { display: block; } }
        @media (max-width: 768px) { .content-wrapper { padding: 16px; } .top-header { padding: 16px; } .country-selector span:not(.flag) { display: none; } .header-right .header-icon { width: 36px; height: 36px; } }
      `}</style>
    </div>
  );
}

export default OnboardingBusinessDashboard;